import { canUseCentralizedCreation, navigateToQuickPublishFlow } from 'app/screens/intro/navigator-helper';
import { useNavigation } from 'app/hooks/navigation';
import { StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { NewListingPrompt, useNewListingPrompt } from 'app/screens/new/prompt';
import { useTutorialRef } from 'app/components/tutorial-overlay';
import { Button } from 'app/design/primitives/button';
import { Box } from 'app/shopify';
import { PublishButton as DesignSystemPublishButton } from 'app/shopify/components/actions/PublishButton/PublishButton';
import { useAuthorize } from 'app/hooks/use-authorize';
import { useUser } from 'app/network/user';
import { TutorialRef } from 'app/types/tutorial';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useIsGuestWeb } from 'app/utils/web-helpers';

export function PublishButton({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation('header');
  const { data: user, isPending: isUserPending } = useUser();
  const centralizedCreation = canUseCentralizedCreation(user?.featureFlags, isUserPending);
  const navigation = useNavigation();
  const { viewRef: publishButtonRef } = useTutorialRef(TutorialRef.PublishButton);
  const isGuestWeb = useIsGuestWeb();

  const canPublish = isGuestWeb || isFeatureEnabled(FeatureFlag.publishListings, user?.featureFlags);

  const withAuthorize = useAuthorize();

  const { hideNewListingPrompt, showNewListingPrompt, newListingPromptState } = useNewListingPrompt();
  const [isPromptVisible] = newListingPromptState;

  const closePrompt = () => {
    hideNewListingPrompt();
  };

  const openPrompt = () => {
    if (isGuestWeb) {
      showNewListingPrompt('+');
      return;
    }
    withAuthorize(() => {
      showNewListingPrompt('+');
    });
  };

  const onButtonPress = () => {
    if (centralizedCreation) {
      withAuthorize(() => navigateToQuickPublishFlow(navigation, user?.featureFlags, isUserPending));
      return;
    }
    if (isPromptVisible) {
      closePrompt();
      return;
    }
    openPrompt();
  };

  if (!canPublish) {
    return null;
  }

  return (
    <Box ref={publishButtonRef} collapsable={false} position="relative" style={{ marginLeft: 'auto' }}>
      {isPromptVisible ? (
        <Button
          variant="plain"
          onPress={closePrompt}
          style={{
            ...StyleSheet.absoluteFill,
            position: 'fixed' as 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9,
          }}
        >
          <Box />
        </Button>
      ) : null}

      <Box position="relative" zIndex={10}>
        <DesignSystemPublishButton
          label={t('publishButton')}
          onPress={onButtonPress}
          size={compact ? 'small' : 'medium'}
          breakpoint="large"
          showChevron={!centralizedCreation}
        />
      </Box>
      {isPromptVisible ? <NewListingPrompt state={newListingPromptState} /> : null}
    </Box>
  );
}
